import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Collapse, Typography } from '@mui/material';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import MusicVideoOutlinedIcon from '@mui/icons-material/MusicVideoOutlined';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../../pages/BrandDashboard/brandDashboardTokens';
import { brandAuthUrl } from '../../configurations/api/url';
import { SocialLogin } from '../SocialLogin/SocialLogin';
import type { ISocialLinks } from '../../redux/buzzbridge/types';
import { formatSocialHandle } from '../../utils/socialHandle';
import { buzzBridgeConfigSelector } from '../../selectors/selectors';
import { isBrandPlatformAutoLinkEnabled } from '../../utils/brandSocialAutoLink';
import { resolveInstagramConnectProvider } from '../../utils/instagramOAuthProvider';
import {
    EMPTY_BRAND_MANUAL_SOCIAL_LINK,
    isBrandManualSocialLinkComplete,
    type IBrandManualSocialLinkData,
} from '../../utils/brandManualSocialLinkUtils';
import { BrandManualSocialLinkForm } from './BrandManualSocialLinkForm';
import { BrandAdHubMetaConnectSection } from '../../pages/BrandDashboard/adhub/BrandAdHubMetaConnectSection';

type TProvider = 'meta' | 'instagram' | 'tiktok' | 'youtube';

export interface SocialPlatform {
    id: keyof ISocialLinks;
    label: string;
    description: string;
    Icon: React.ElementType;
    recommended?: boolean;
    oauthProvider?: TProvider;
}

const PLATFORMS: SocialPlatform[] = [
    {
        id: 'instagram',
        label: 'Instagram',
        description: 'Sync your feed and highlights.',
        Icon: CameraAltOutlinedIcon,
        recommended: true,
        // Resolved at render via resolveInstagramConnectProvider (Instagram Login when enabled).
        oauthProvider: 'instagram',
    },
    {
        id: 'tiktok',
        label: 'TikTok',
        description: 'Verify your short-form reach.',
        Icon: MusicVideoOutlinedIcon,
        oauthProvider: 'tiktok',
    },
    {
        id: 'youtube',
        label: 'YouTube',
        description: 'Import long-form brand content.',
        Icon: SmartDisplayOutlinedIcon,
        oauthProvider: 'youtube',
    },
];

export interface BrandSocialIntegrationProps {
    /** Brand id for Meta ad account (AdHub) connect card. */
    brandId?: string | null;
    connectedLinks?: Partial<ISocialLinks>;
    /** Platforms connected via OAuth (vs manual URL entry). */
    oauthLinkedPlatforms?: Partial<Record<keyof ISocialLinks, boolean>>;
    connectingPlatform?: keyof ISocialLinks | null;
    savingManualPlatform?: keyof ISocialLinks | null;
    initialManualLinks?: Partial<Record<keyof ISocialLinks, IBrandManualSocialLinkData>>;
    onDisconnect?: (platformId: keyof ISocialLinks) => void;
    onConnecting?: (platformId: keyof ISocialLinks) => void;
    onOAuthCode?: (code: string, provider: string, platformId: keyof ISocialLinks) => void;
    onAuthFailed?: (platformId: keyof ISocialLinks) => void;
    onManualSave?: (platformId: keyof ISocialLinks, data: IBrandManualSocialLinkData) => Promise<void>;
    onSkip?: () => void;
}

export function BrandSocialIntegration({
    brandId,
    connectedLinks = {},
    oauthLinkedPlatforms = {},
    connectingPlatform,
    savingManualPlatform,
    initialManualLinks = {},
    onDisconnect,
    onConnecting,
    onOAuthCode,
    onAuthFailed,
    onManualSave,
    onSkip,
}: BrandSocialIntegrationProps) {
    const buzzBridgeConfig = useSelector(buzzBridgeConfigSelector);
    const [manualEntryOpen, setManualEntryOpen] = useState<keyof ISocialLinks | null>(null);
    const [manualDrafts, setManualDrafts] = useState<Partial<Record<keyof ISocialLinks, IBrandManualSocialLinkData>>>(
        {},
    );

    const manualDataFor = (id: keyof ISocialLinks) =>
        manualDrafts[id] ?? initialManualLinks[id] ?? EMPTY_BRAND_MANUAL_SOCIAL_LINK;

    const autoLinkByPlatform = useMemo(
        () =>
            Object.fromEntries(
                PLATFORMS.map(p => [p.id, isBrandPlatformAutoLinkEnabled(buzzBridgeConfig, p.id)]),
            ) as Record<keyof ISocialLinks, boolean>,
        [buzzBridgeConfig],
    );

    const handleManualSave = async (id: keyof ISocialLinks) => {
        const data = manualDataFor(id);
        if (!isBrandManualSocialLinkComplete(data) || !onManualSave) return;
        await onManualSave(id, data);
        setManualEntryOpen(null);
    };

    return (
        <Box sx={{ maxWidth: 680, mx: 'auto', width: '100%' }}>
            <Typography sx={{ fontSize: 14, color: '#adaaaa', textAlign: 'center', lineHeight: 1.7, mb: 4 }}>
                Connect your brand&apos;s digital presence to verify authenticity and streamline content rights
                management.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {PLATFORMS.map(({ id, label, description, Icon, recommended, oauthProvider }) => {
                    const connected = !!connectedLinks[id];
                    const handle = connectedLinks[id];
                    const isOAuthLinked = Boolean(oauthLinkedPlatforms[id]);
                    const autoLinkEnabled = autoLinkByPlatform[id];
                    const isConnecting = connectingPlatform === id;
                    const isSavingManual = savingManualPlatform === id;
                    const anyConnecting = !!connectingPlatform;
                    const manualOpen = manualEntryOpen === id;
                    const manualComplete = isBrandManualSocialLinkComplete(manualDataFor(id));

                    const oauthLinkButton = (
                        <Button
                            className="link-btn"
                            fullWidth
                            disabled={isConnecting || (anyConnecting && !connected)}
                            startIcon={isConnecting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined}
                            sx={{
                                py: 1.25,
                                borderRadius: 999,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 14,
                                textTransform: 'none',
                                transition: 'all 0.2s',
                                bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                color: '#fff',
                                '&.Mui-disabled': {
                                    bgcolor: isConnecting ? BRAND_SURFACE_CONTAINER_HIGH : undefined,
                                    color: isConnecting ? '#fff' : 'rgba(255,255,255,0.3)',
                                },
                            }}>
                            {isConnecting ? 'Connecting...' : 'Link Account'}
                        </Button>
                    );

                    const disconnectOAuthButton = (
                        <Button
                            className="link-btn"
                            fullWidth
                            onClick={() => onDisconnect?.(id)}
                            sx={{
                                py: 1.25,
                                borderRadius: 999,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 14,
                                textTransform: 'none',
                                bgcolor: 'rgba(38,38,38,0.5)',
                                color: '#adaaaa',
                            }}>
                            Disconnect
                        </Button>
                    );

                    const manualFormPanel = (
                        <Collapse in={manualOpen}>
                            <BrandManualSocialLinkForm
                                platformId={id}
                                platformLabel={label}
                                value={manualDataFor(id)}
                                onChange={data =>
                                    setManualDrafts(prev => ({
                                        ...prev,
                                        [id]: data,
                                    }))
                                }
                            />
                            <Button
                                fullWidth
                                disabled={!manualComplete || isSavingManual || !onManualSave}
                                onClick={() => void handleManualSave(id)}
                                sx={{
                                    mt: 2,
                                    py: 1.25,
                                    borderRadius: 999,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 14,
                                    textTransform: 'none',
                                    color: '#392700',
                                    bgcolor: BRAND_LUXE_PRIMARY,
                                    '&:hover': { filter: 'brightness(1.05)' },
                                    '&.Mui-disabled': { opacity: 0.45, color: '#392700' },
                                }}>
                                {isSavingManual ? (
                                    <CircularProgress size={18} sx={{ color: '#392700' }} />
                                ) : (
                                    'Save profile'
                                )}
                            </Button>
                        </Collapse>
                    );

                    const manualToggleButton = (
                        <Button
                            fullWidth
                            onClick={() => setManualEntryOpen(prev => (prev === id ? null : id))}
                            startIcon={connected ? <EditOutlinedIcon sx={{ fontSize: 16 }} /> : undefined}
                            sx={{
                                py: 1.25,
                                borderRadius: 999,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 14,
                                textTransform: 'none',
                                bgcolor: autoLinkEnabled && !connected ? 'transparent' : BRAND_SURFACE_CONTAINER_HIGH,
                                color: autoLinkEnabled && !connected ? BRAND_LUXE_PRIMARY : '#fff',
                                border: autoLinkEnabled && !connected ? '1px dashed rgba(255,201,101,0.35)' : 'none',
                                '&:hover': {
                                    bgcolor:
                                        autoLinkEnabled && !connected
                                            ? 'rgba(255,201,101,0.08)'
                                            : BRAND_SURFACE_CONTAINER_HIGH,
                                },
                            }}>
                            {manualOpen ? 'Close' : connected ? 'Edit manual profile' : 'Add manually'}
                        </Button>
                    );

                    const renderConnectControl = () => {
                        if (connected && isOAuthLinked) {
                            return disconnectOAuthButton;
                        }

                        if (connected) {
                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Button
                                            fullWidth
                                            onClick={() => onDisconnect?.(id)}
                                            sx={{
                                                py: 1.25,
                                                borderRadius: 999,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 14,
                                                textTransform: 'none',
                                                bgcolor: 'rgba(38,38,38,0.5)',
                                                color: '#adaaaa',
                                            }}>
                                            Disconnect
                                        </Button>
                                        {manualToggleButton}
                                    </Box>
                                    {manualFormPanel}
                                </Box>
                            );
                        }

                        return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {autoLinkEnabled && oauthProvider ? (
                                    <SocialLogin
                                        provider={
                                            id === 'instagram'
                                                ? resolveInstagramConnectProvider(buzzBridgeConfig)
                                                : oauthProvider!
                                        }
                                        authBaseUrl={brandAuthUrl}
                                        onClick={() => onConnecting?.(id)}
                                        onCodeReceived={code =>
                                            onOAuthCode?.(
                                                code,
                                                id === 'instagram'
                                                    ? resolveInstagramConnectProvider(buzzBridgeConfig)
                                                    : oauthProvider!,
                                                id,
                                            )
                                        }
                                        onAuthComplete={success => {
                                            if (!success) onAuthFailed?.(id);
                                        }}
                                        customButton={oauthLinkButton}
                                    />
                                ) : null}
                                {manualToggleButton}
                                {manualFormPanel}
                            </Box>
                        );
                    };

                    return (
                        <Box
                            key={id}
                            sx={{
                                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                p: 3,
                                borderRadius: 3,
                                border: '1px solid transparent',
                                transition: 'all 0.2s',
                                gridColumn: manualOpen ? { sm: '1 / -1' } : undefined,
                                '&:hover': {
                                    bgcolor: BRAND_SURFACE_CONTAINER,
                                    borderColor: 'rgba(255,201,101,0.15)',
                                    '& .link-btn': connected ? {} : { bgcolor: BRAND_LUXE_PRIMARY, color: '#392700' },
                                },
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '50%',
                                        bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    <Icon sx={{ fontSize: 24, color: '#fff' }} />
                                </Box>
                                {connected ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            color: BRAND_LUXE_SECONDARY,
                                        }}>
                                        <CheckCircleIcon sx={{ fontSize: 14 }} />
                                        <Typography
                                            sx={{
                                                fontSize: 10,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.1em',
                                            }}>
                                            {isOAuthLinked ? 'Connected' : 'Manual'}
                                        </Typography>
                                    </Box>
                                ) : recommended ? (
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.1em',
                                            py: 0.5,
                                            px: 1.5,
                                            bgcolor: 'rgba(0,253,193,0.1)',
                                            color: BRAND_LUXE_SECONDARY,
                                            borderRadius: 999,
                                        }}>
                                        Recommended
                                    </Typography>
                                ) : (
                                    <LinkIcon
                                        sx={{
                                            fontSize: 20,
                                            color: '#adaaaa',
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            '.MuiBox-root:hover &': { opacity: 1 },
                                        }}
                                    />
                                )}
                            </Box>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff', mb: 0.5 }}>
                                {label}
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: '#adaaaa', mb: 3 }}>
                                {connected && handle
                                    ? formatSocialHandle(handle)
                                    : !autoLinkEnabled
                                    ? 'OAuth is off — enter your handle and we’ll build the profile URL.'
                                    : description}
                            </Typography>

                            {renderConnectControl()}
                        </Box>
                    );
                })}
                {brandId ? <BrandAdHubMetaConnectSection brandId={brandId} variant="socialCard" /> : null}
            </Box>

            <Box
                sx={{
                    mt: 4,
                    p: 2.5,
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: BRAND_SURFACE_CONTAINER,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                    <HelpOutlineIcon sx={{ fontSize: 20, color: BRAND_LUXE_PRIMARY }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: '#fff' }}>
                        Don&apos;t have these platforms?
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#adaaaa' }}>
                        Use Link Account for OAuth, or Add manually to enter a profile URL (saved with linked: false).
                    </Typography>
                </Box>
                {onSkip && (
                    <Button
                        onClick={onSkip}
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: BRAND_LUXE_PRIMARY,
                            textTransform: 'none',
                            textDecoration: 'underline',
                            textUnderlineOffset: 3,
                            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                        }}>
                        Skip Now
                    </Button>
                )}
            </Box>
        </Box>
    );
}
